import { initializeApp, getApps } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { isWeb } from '../utils/platformUtils';
import { notificationsAPI } from './api';

const PUSH_PROMPT_KEY = 'santa-casa-push-prompt';
const PUSH_OPTED_OUT_KEY = 'santa-casa-push-opted-out';
const FCM_TOKEN_KEY = 'santa-casa-fcm-token';
const PUSH_PROMPT_SNOOZE_MONTHS = 3;
const APP_VERSION = process.env.REACT_APP_VERSION || '1.0.0';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let messagingInstance = null;
let foregroundUnsubscribe = null;

const hasFirebaseConfig = () =>
  Boolean(
    firebaseConfig.apiKey &&
      firebaseConfig.projectId &&
      firebaseConfig.messagingSenderId &&
      firebaseConfig.appId &&
      vapidKey
  );

const getFirebaseApp = () => {
  if (getApps().length) return getApps()[0];
  return initializeApp(firebaseConfig);
};

const parsePushPromptRecord = (raw) => {
  if (!raw) return null;
  if (raw === 'dismissed' || raw === 'accepted') {
    return { status: raw, at: null };
  }
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed.status === 'string') {
      return { status: parsed.status, at: Number(parsed.at) || null };
    }
  } catch (_) {
    return null;
  }
  return null;
};

const getPushPromptRecord = () => {
  try {
    return parsePushPromptRecord(localStorage.getItem(PUSH_PROMPT_KEY));
  } catch (_) {
    return null;
  }
};

export const getPushPromptStatus = () => getPushPromptRecord()?.status || null;

export const setPushPromptStatus = (status) => {
  try {
    if (status == null) {
      localStorage.removeItem(PUSH_PROMPT_KEY);
      return;
    }
    localStorage.setItem(PUSH_PROMPT_KEY, JSON.stringify({ status, at: Date.now() }));
  } catch (_) {
    // ignore
  }
};

const isDismissedSnoozeExpired = (record) => {
  if (!record || record.status !== 'dismissed') return false;
  if (record.at == null) return true;
  const resumeAt = new Date(record.at);
  resumeAt.setMonth(resumeAt.getMonth() + PUSH_PROMPT_SNOOZE_MONTHS);
  return Date.now() >= resumeAt.getTime();
};

export const isPushOptedOut = () => {
  try {
    return localStorage.getItem(PUSH_OPTED_OUT_KEY) === '1';
  } catch (_) {
    return false;
  }
};

export const setPushOptedOut = (optedOut) => {
  try {
    if (optedOut) localStorage.setItem(PUSH_OPTED_OUT_KEY, '1');
    else localStorage.removeItem(PUSH_OPTED_OUT_KEY);
  } catch (_) {
    // ignore
  }
};

export const getStoredFcmToken = () => {
  try {
    return localStorage.getItem(FCM_TOKEN_KEY);
  } catch (_) {
    return null;
  }
};

const storeFcmToken = (token) => {
  try {
    if (token) localStorage.setItem(FCM_TOKEN_KEY, token);
    else localStorage.removeItem(FCM_TOKEN_KEY);
  } catch (_) {
    // ignore
  }
};

/**
 * true se ainda devemos mostrar o modal de opt-in neste navegador.
 */
export const shouldShowPushOptInPrompt = () => {
  if (!isWebPushConfigured()) return false;
  if (typeof Notification === 'undefined') return false;
  if (Notification.permission !== 'default') return false;
  const record = getPushPromptRecord();
  if (!record) return true;
  if (record.status === 'accepted') return false;
  if (record.status === 'dismissed') return isDismissedSnoozeExpired(record);
  return true;
};

/**
 * Inicializa o Messaging apenas no navegador (não no app Capacitor).
 */
export const getFirebaseMessaging = async () => {
  if (!isWeb() || !hasFirebaseConfig()) return null;
  if (typeof window === 'undefined') return null;

  const supported = await isSupported().catch(() => false);
  if (!supported) return null;

  if (!messagingInstance) {
    messagingInstance = getMessaging(getFirebaseApp());
  }
  return messagingInstance;
};

export const isWebPushConfigured = () => isWeb() && hasFirebaseConfig();

export const getNotificationPermission = () => {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
};

export const isPushEnabledOnThisBrowser = () =>
  !isPushOptedOut() &&
  getNotificationPermission() === 'granted' &&
  !!getStoredFcmToken();

/**
 * Registra o service worker do FCM e espera ele ficar ativo.
 */
export const registerMessagingServiceWorker = async () => {
  if (!('serviceWorker' in navigator)) return null;

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');

  try {
    await registration.update();
  } catch (_) {
    // ignore
  }

  if (registration.installing) {
    await new Promise((resolve) => {
      registration.installing.addEventListener('statechange', (event) => {
        if (event.target.state === 'activated') resolve();
      });
    });
  } else if (registration.waiting) {
    registration.waiting.postMessage?.({ type: 'SKIP_WAITING' });
  }

  await navigator.serviceWorker.ready;
  return registration;
};

/**
 * Deve ser chamado no início do handler de clique (sem await antes),
 * senão o Chrome bloqueia o prompt de permissão.
 */
export const requestNotificationPermissionNow = () => {
  if (typeof Notification === 'undefined') {
    return Promise.resolve('denied');
  }
  if (Notification.permission !== 'default') {
    return Promise.resolve(Notification.permission);
  }
  return Notification.requestPermission();
};

/**
 * Obtém token FCM. Se `permission` for passado, não chama requestPermission de novo.
 * @param {{ permission?: NotificationPermission }} options
 */
export const enableWebPushNotifications = async ({ permission: knownPermission } = {}) => {
  const messaging = await getFirebaseMessaging();
  if (!messaging) {
    return {
      ok: false,
      reason: !isWeb()
        ? 'unsupported_platform'
        : !hasFirebaseConfig()
          ? 'missing_config'
          : 'unsupported_browser',
      token: null,
    };
  }

  const permission =
    knownPermission ||
    (typeof Notification !== 'undefined' ? Notification.permission : 'denied');

  if (permission !== 'granted') {
    return { ok: false, reason: 'permission_denied', token: null };
  }

  const registration = await registerMessagingServiceWorker();
  const token = await getToken(messaging, {
    vapidKey,
    ...(registration ? { serviceWorkerRegistration: registration } : {}),
  });

  if (!token) {
    return { ok: false, reason: 'no_token', token: null };
  }

  storeFcmToken(token);

  if (process.env.NODE_ENV === 'development') {
    console.info('[FCM] Token web:', token);
  }

  return { ok: true, reason: null, token };
};

/**
 * Obtém token (se já tiver permissão) e faz UPSERT no backend.
 * Para ativar no clique do usuário, chame requestNotificationPermissionNow()
 * ANTES e passe o resultado em `permission`.
 */
export const syncPushDeviceWithBackend = async ({
  contrato,
  permission,
} = {}) => {
  if (!isWebPushConfigured()) {
    return { ok: false, reason: 'missing_config', token: null };
  }

  const tokenResult = await enableWebPushNotifications({ permission });
  if (!tokenResult.ok) return tokenResult;

  try {
    await notificationsAPI.syncDevice({
      fcmToken: tokenResult.token,
      platform: 'web',
      appVersion: APP_VERSION,
      ...(contrato ? { contrato: String(contrato) } : {}),
    });
    setPushPromptStatus('accepted');
    setPushOptedOut(false);
    return { ok: true, reason: null, token: tokenResult.token };
  } catch (err) {
    console.error('[FCM] Falha ao sincronizar device:', err);
    return {
      ok: false,
      reason: 'sync_failed',
      token: tokenResult.token,
      error: err,
    };
  }
};

/**
 * Desativa push neste device no backend (não revoga permission do navegador).
 */
export const disablePushDeviceOnBackend = async (fcmToken) => {
  const token = fcmToken || getStoredFcmToken();

  // Marca opt-out local ANTES do sync pós-login poder reativar
  setPushOptedOut(true);

  if (!token) {
    storeFcmToken(null);
    return { ok: true, reason: 'no_token' };
  }

  try {
    await notificationsAPI.disableDevice({ fcmToken: token });
    storeFcmToken(null);
    return { ok: true, reason: null };
  } catch (err) {
    console.error('[FCM] Falha ao desativar device:', err);
    // Mesmo com erro de rede, mantém opt-out local para não reativar no próximo F5
    storeFcmToken(null);
    return { ok: false, reason: 'disable_failed', error: err };
  }
};

/**
 * Após login/restore: sync se já tiver permissão; senão indica se deve abrir modal.
 */
export const handlePushAfterAuth = async ({ contrato } = {}) => {
  if (!isWebPushConfigured()) {
    return { action: 'skip', reason: 'not_configured' };
  }

  // Usuário desativou em Perfil — não re-sincronizar automaticamente
  if (isPushOptedOut()) {
    return { action: 'skip', reason: 'opted_out' };
  }

  const permission = getNotificationPermission();

  if (permission === 'granted') {
    const result = await syncPushDeviceWithBackend({
      contrato,
      permission: 'granted',
    });
    return { action: 'synced', result };
  }

  if (permission === 'denied') {
    setPushPromptStatus('dismissed');
    return { action: 'skip', reason: 'permission_denied' };
  }

  if (shouldShowPushOptInPrompt()) {
    return { action: 'show_prompt' };
  }

  return { action: 'skip', reason: 'prompt_already_answered' };
};

export const listenForegroundMessages = async (onPayload) => {
  if (foregroundUnsubscribe) {
    foregroundUnsubscribe();
    foregroundUnsubscribe = null;
  }

  const messaging = await getFirebaseMessaging();
  if (!messaging || typeof onPayload !== 'function') return () => {};

  foregroundUnsubscribe = onMessage(messaging, (payload) => {
    onPayload(payload);
  });

  return () => {
    if (foregroundUnsubscribe) {
      foregroundUnsubscribe();
      foregroundUnsubscribe = null;
    }
  };
};
